import React, { useEffect, useMemo, useState } from 'react'
import { RouteProps } from 'react-router-dom'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import OnboardingTemplate, { OnboardingTemplateProps } from '../../templates/Onboarding'
import { useHeader } from '../../relay/Header/useHeader'
import { useFooter } from '../../relay/Footer/useFooter'
import { Icons } from '../../components/Icon'
import { ActionButtonLook, ActionButtonVariant } from '../../components/ActionButton/styled'
import { actions, selectors } from '../../redux'
import { router, routesPath } from '../../router'
import { PickCCIValues } from '../../components/forms/OnboardingForm/PickCCI'

const OnboardingPage: React.FC<RouteProps> = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const header = useHeader()
  const footer = useFooter()
  const [step, setStep] = useState(0)
  const [currentEmail, setCurrentEmail] = useState<string>('')

  const login = useSelector(selectors.auth.login)
  const verifyTwoFactor = useSelector(selectors.auth.verifyTwoFactor)
  const institution = useSelector(selectors.institutions.institution)
  const { data: institutions } = useSelector(selectors.institutions.institutions)

  useEffect(() => {
    dispatch(actions.institutions.institutionsRequest())
    if (login.success) {
      setStep(2)
    }
  }, [dispatch, login.success])

  const templateProps: OnboardingTemplateProps = useMemo(
    () => ({
      layout: {
        header,
        footer,
      },
      onboardingForm: {
        currentStep: step,
        background: `${import.meta.env.BASE_URL}images/onboarding/pickCCI.jpg`,
        pickCCI: {
          header: {
            title: t('onboarding.title'),
          },
          content: {
            title: t('onboarding.pickCCI.title'),
            formikForm: {
              initialValues: {
                cci: '',
              },
              validationSchema: Yup.object().shape({
                cci: Yup.string().required(t('onboarding.pickCCI.select.error')),
              }),
              onSubmit: (values: PickCCIValues) => {
                const institution = institutions?.find((i) => values.cci === i.id)

                if (institution) {
                  dispatch(actions.institutions.setInstitution({ institution }))
                  setStep(1)
                }
              },
              validateOnChange: true,
            },
            fields: {
              cci: {
                label: t('onboarding.pickCCI.select.label'),
                name: 'cci',
                placeholder: t('onboarding.pickCCI.select.placeholder'),
                options: institutions
                  ? institutions?.map((institution) => ({
                      label: institution.name,
                      value: institution.id,
                    }))
                  : [],
              },
            },
            submitButton: {
              text: t('onboarding.pickCCI.submit.label'),
              iconProps: {
                icon: Icons.arrowRight,
              },
              iconPosition: 'right',
            },
            errors: login.errors,
          },
        },
        login: {
          header: {
            title: 'Bonjour',
            headerButton: {
              text: 'Retour',
              variant: ActionButtonVariant.white,
              look: ActionButtonLook.outlined,
              iconProps: {
                icon: Icons.arrowLeft,
              },
              iconPosition: 'left',
              onClick: () => setStep(0),
            },
          },
          content: {
            title: t('onboarding.login.title'),
            formikForm: {
              initialValues: {
                email: '',
                password: '',
              },
              validationSchema: Yup.object().shape({
                email: Yup.string().required().email(),
                password: Yup.string().required(),
              }),
              onSubmit: (values) => {
                dispatch(
                  actions.auth.loginRequest({
                    email: values.email,
                    password: values.password,
                  })
                )
                setCurrentEmail(values.email)
              },
              validateOnChange: false,
            },
            fields: {
              email: {
                name: 'email',
                label: t('onboarding.login.email.label'),
                placeholder: t('onboarding.login.email.placeholder'),
                required: true,
              },
              password: {
                name: 'password',
                label: t('onboarding.login.password.label'),
                placeholder: t('onboarding.login.password.placeholder'),
                required: true,
              },
            },
            forgotPassword: {
              text: t('onboarding.login.forgotPassword'),
              href: router(routesPath.forgotPassword),
            },
            errors: login.errors,
            submitButton: {
              text: t('onboarding.login.submit.label'),
              isPending: login.pending,
              isSuccess: login.success,
            },
            textDivider: t('onboarding.login.divider'),
            sso: {
              text: t('onboarding.login.sso.title'),
              variant: ActionButtonVariant.black,
              look: ActionButtonLook.outlined,
              iconProps: {
                icon: Icons.microsoft,
              },
              iconPosition: 'left',
              type: 'button',
              onClick: () => {
                if (institution?.redirect_uri) {
                  window.open(institution.redirect_uri, '_self')
                }
              },
            },
          },
        },
        twoFactorAuth: {
          header: {
            title: t('onboarding.twoFactorAuth.title'),
            headerButton: {
              text: t('onboarding.twoFactorAuth.headerButton.text'),
              variant: ActionButtonVariant.white,
              look: ActionButtonLook.outlined,
              iconProps: {
                icon: Icons.arrowLeft,
              },
              iconPosition: 'left',
              onClick: () => setStep(1),
            },
          },
          content: {
            title: t('onboarding.twoFactorAuth.content.title'),
            html: {
              text: t('onboarding.twoFactorAuth.content.text'),
            },
            formikForm: {
              initialValues: {
                code: '',
              },
              validationSchema: Yup.object().shape({
                code: Yup.string().required(),
              }),
              onSubmit: (values) => {
                dispatch(
                  actions.auth.verifyTwoFactorRequest({
                    email: currentEmail,
                    code: values.code,
                  })
                )
              },
              validateOnChange: true,
            },
            fields: {
              code: {
                label: t('onboarding.twoFactorAuth.code.label'),
                name: 'code',
              },
            },
            errors: verifyTwoFactor.errors,
            notReceivedCodeButton: {
              text: t('onboarding.twoFactorAuth.notReceivedCodeButton.label'),
              variant: ActionButtonVariant.whiteWithBorder,
              onClick: () => setStep(1),
            },
            submitButton: {
              text: t('onboarding.twoFactorAuth.submit.label'),
            },
          },
        },
      },
    }),
    [
      dispatch,
      footer,
      header,
      login.errors,
      login.pending,
      login.success,
      step,
      t,
      institutions,
      institution,
      currentEmail,
      verifyTwoFactor,
    ]
  )

  return <OnboardingTemplate {...templateProps} />
}

export default OnboardingPage
